import React from "react";
import {formatDate} from "../../utils";
import type {WorkflowStageContext} from "./stage";
import {ReceiptStage, SectionTitle, stageSectionClass} from "./WorkflowStageControls";
import type {ExchangeSellPanelState} from "../../api";
import {formatRubKopecks, usdtAmountToAtomic} from "../../api";

export function sellDepositConfirmed(status: string | undefined, confirmedAt: string | null | undefined): boolean {
    return status === "confirmed" && Boolean(confirmedAt);
}

export function sellDepositStatusLabel(status: string | undefined): string {
    switch (status) {
        case "waiting_address": return "ожидается адрес";
        case "expected": return "ожидается поступление";
        case "confirmed": return "подтверждено сетью";
        case "review_required": return "нужен разбор";
        case "expired": return "срок истёк";
        default: return status || "адрес ещё не выдан";
    }
}

export function sellReviewReasonLabel(reason: string | null | undefined): string {
    switch (reason) {
        case "amount_mismatch": return "сумма не совпадает с ожидаемой";
        case "unknown_transaction": return "транзакция не распознана";
        case "late_deposit": return "поступление после срока";
        default: return reason || "нестандартное поступление";
    }
}

export function sellCompletionProofReady(state: ExchangeSellPanelState | null | undefined, hasReceipt: boolean): boolean {
    return Boolean(state?.canCompleteRub && sellDepositConfirmed(state.deposit?.status, state.deposit?.confirmedAt) && hasReceipt);
}

function formatUsdtAtomic(value: bigint): string {
    const digits = value.toString().padStart(7, "0");
    return `${digits.slice(0, -6)}.${digits.slice(-6)}`;
}

function depositMismatch(deposit: ExchangeSellPanelState["deposit"] | undefined): {kind: "underpayment" | "overpayment"; difference: string} | null {
    if (!deposit?.expectedAmount || !deposit.receivedAmount) return null;
    const expected = usdtAmountToAtomic(deposit.expectedAmount);
    const received = usdtAmountToAtomic(deposit.receivedAmount);
    if (expected == null || received == null || expected === received) return null;
    return {kind: received < expected ? "underpayment" : "overpayment", difference: formatUsdtAtomic(received < expected ? expected - received : received - expected)};
}

// Keep the publish response visible only while the payment stream still has
// the pre-publish empty waiting snapshot. Every published or later snapshot
// wins, including same-status updates carrying signals, expiry, or review.
export function selectActiveSellState(
    authoritative: ExchangeSellPanelState | null,
    optimistic: ExchangeSellPanelState | null,
): ExchangeSellPanelState | null {
    if (!optimistic || !authoritative) return optimistic ?? authoritative;
    const serverDeposit = authoritative.deposit;
    if (serverDeposit?.status === "waiting_address" && !serverDeposit.address && !serverDeposit.publishedAt) return optimistic;
    return authoritative;
}

export function ExchangeSell({context}: {context: WorkflowStageContext}): JSX.Element {
    const {payment, sell} = context;
    const state = sell?.state;
    const deposit = state?.deposit;
    const published = Boolean(deposit?.publishedAt || deposit?.address);
    const confirmed = sellDepositConfirmed(deposit?.status, deposit?.confirmedAt);
    const expired = Boolean(deposit?.expiredAt);
    const reviewBlocked = Boolean(state?.reviewBlocking);
    const mismatch = depositMismatch(deposit);
    return (
        <>
            <section className={stageSectionClass(published ? "done" : "current")}>
                <SectionTitle number="1" state={published ? "done" : "current"} hint="только чтение">Продажа USDT: адрес и курс</SectionTitle>
                <div className="details-info-card">
                    <div className="details-info-row"><span>Курс продажи</span><strong className="mono">{state?.rate ?? payment.rateFixed ?? "—"} RUB/USDT</strong></div>
                    {state?.clientRef && <div className="details-info-row"><span>Клиент</span><strong className="mono">{state.clientRef}</strong></div>}
                    <div className="details-info-row"><span>Ожидается USDT</span><strong className="mono">{deposit?.expectedAmount ?? "—"}</strong></div>
                    {state?.terms && <>
                        <div className="details-info-row"><span>Исходная сумма</span><strong className="mono">{state.terms.originalUsdtAmount} USDT · {formatRubKopecks(state.terms.originalRubKop)}</strong></div>
                        <div className="details-info-row"><span>Текущая сумма</span><strong className="mono">{state.terms.usdtAmount} USDT · {formatRubKopecks(state.terms.rubKop)}</strong></div>
                    </>}
                    {deposit?.address && <div className="details-info-row"><span>TRC20 адрес</span><strong className="mono">{deposit.address}</strong></div>}
                </div>
                {!published && (
                    <button className="btn btn-primary" type="button" onClick={sell?.onPublish} disabled={reviewBlocked || sell?.publishing || !state}>
                        {sell?.publishing ? "Выдаём адрес…" : "Выдать TRC"}
                    </button>
                )}
                {sell?.error && <div className="details-action-error">{sell.error}</div>}
            </section>
            <section className={stageSectionClass(confirmed ? "done" : published ? "current" : "ahead")}>
                <SectionTitle number="2" state={confirmed ? "done" : published ? "current" : "ahead"} hint={deposit?.network ?? "TRC20"}>Депозит USDT</SectionTitle>
                <div className="details-info-card">
                    <div className="details-info-row"><span>Статус</span><strong>{sellDepositStatusLabel(deposit?.status)}</strong></div>
                    <div className="details-info-row"><span>Получено</span><strong className="mono">{deposit?.receivedAmount ?? "—"} USDT</strong></div>
                    {mismatch && <div className="details-info-row"><span>{mismatch.kind === "underpayment" ? "Недоплата" : "Переплата"}</span><strong className="mono">{mismatch.difference} USDT</strong></div>}
                    <div className="details-info-row"><span>Срок</span><strong>{deposit?.deadline ? formatDate(deposit.deadline) : "—"}</strong></div>
                    {expired && <div className="details-lock-hint" role="status">Срок вышел. Спор открыт, но это не блокирует позднее подтверждение депозита.</div>}
                    {state?.clientMarkedPaidAt && <div className="details-lock-hint" role="status">Клиент сообщил об оплате — это сигнал, не подтверждение сети.</div>}
                    {state?.reviewRequired && <div className="details-lock-hint" role="status">Нужен разбор: {sellReviewReasonLabel(state.reviewReason)}</div>}
                    {reviewBlocked && <div className="details-lock-hint" role="status">Обработка заблокирована администратором до согласования суммы депозита. Владелец заявки сохраняется.</div>}
                </div>
            </section>
            <section className={stageSectionClass(confirmed ? "current" : "ahead")}>
                <SectionTitle number="3" state={confirmed ? "current" : "ahead"} hint="рубли после подтверждения">Выплата RUB</SectionTitle>
                <div className="details-info-card">
                    <div className="details-info-row"><span>ФИО</span><strong>{payment.clientFio || "—"}</strong></div>
                    <div className="details-info-row"><span>Карта / счёт</span><strong className="mono">{payment.clientCard || "—"}</strong></div>
                    <div className="details-info-row"><span>Банк</span><strong>{payment.clientBank || payment.bankId || "—"}</strong></div>
                </div>
                <ReceiptStage number="4" title="Подтверждение выплаты RUB" hint="обязательно" context={context} disabled={reviewBlocked}/>
                <div className="details-lock-hint" role="status">
                    {state?.canCompleteRub ? "Депозит подтверждён. Можно завершить выплату RUB." : "Ожидается точное подтверждение депозита USDT."}
                </div>
                {state?.canCompleteRub && <button className="btn btn-primary" type="button" onClick={context.complete.onRequest} disabled={reviewBlocked || !context.complete.canRequest || context.complete.submitting}>{context.complete.submitting ? "Завершаем…" : context.complete.label}</button>}
                {!state?.canCompleteRub && context.complete.blockedReason && <div className="details-lock-hint">{context.complete.blockedReason}</div>}
            </section>
        </>
    );
}
